import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import { tracked } from '@glimmer/tracking'
import { on } from '@ember/modifier'
import { modifier } from 'ember-modifier'

const containerId = 'dialog-container'

/** The place where dialogs will render into, if not rendered in place */
export const DialogContainer: TOC<unknown> = <template><div id={{containerId}} /></template>

interface Signature {
  Element: HTMLDialogElement
  Args: {
    /** Set to true to display the default transparent grey backdrop */
    showBackdrop?: boolean
    /** A function that is called afrer the dialog is closed */
    onClose?: () => unknown
    /** Set to true to ignore clicks outside the dialog, which would normally close the dialog automatically */
    noOutsideClose?: boolean
    /** Set to true to not close the dialog. Useful for save tasks eg @keepOpenWhile={{@save.isRunning}} */
    keepOpenWhile?: boolean
  }
  Blocks: {
    default: [close: () => void]
  }
}

/** A simple Dialog component to show a native html dialog, handling click outside and escape events. To close, either
 * call the yielded close action, or use a conditional in template to remove the component from DOM.
 *
 * The dialog will be rendered wherever the DialogContainer component is located (for example, in the body or the
 * application template). If not found it will render 'in place' wherever the Dialog component was used.
 */
export class Dialog extends Component<Signature> {
  @tracked element?: HTMLDialogElement

  targetElement = document.getElementById(containerId)

  modal = modifier((element: HTMLDialogElement) => {
    this.element = element
    /*
     * show() as regular dialog, rather than showModal() (which would place it in the top-layer, provide an automatic
     * backdrop element, block all clicks & focus outside of that dialog). These are things we want, but we cannot yet
     * use the modal implementation of the Dialog API because it would involve a larger rework of dropdowns & tooltips
     * to ensure they appear on top of the dialog if one is open. This could be done in two ways;
     * 1) render dropdowns/tooltips "in place" (no portaling - ie if they are in a dialog they will remain in a dialog,
     * so will be part of the modal)
     * OR
     * 2) implement tooltips and dropdowns also in the top-layer (via the HTML Popover API) so that they can still
     * appear on top of modals. This is not too hard, but as of writing this issue means that wormholed/portaled items
     * opened from a modal (eg a dropdown) are not interactive https://github.com/whatwg/html/issues/9936
     * Also noting that while Safari supports Dialog API back till Safari 15, it only supports Popover API to Safari 17
     * (still recent at time of writing)
     */
    this.element.show()
  })

  handleOutsideClick = () => {
    if (!this.args.noOutsideClose) {
      this.close()
    }
  }

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.handleEscape(event)
    }
  }

  handleEscape = (event: KeyboardEvent) => {
    event.preventDefault()
    this.close()
  }

  close = () => {
    if (this.args.keepOpenWhile) return
    this.element?.close()
    this.args.onClose?.()
  }

  <template>
    <MaybeInPlace @targetElement={{this.targetElement}}>
      {{#if @showBackdrop}}
        <button
          data-test-dialog-backdrop
          type="button"
          class="fixed left-0 top-0 z-modal h-screen w-screen bg-modal-overlay"
          {{on "click" this.handleOutsideClick}}
        />
      {{/if}}
      <dialog
        class="fixed inset-[unset] left-1/2 top-1/2 z-modal -translate-x-1/2 -translate-y-1/2 transform shadow-2xl"
        {{this.modal}}
        {{! template-lint-disable no-invalid-interactive }}
        {{on "keydown" this.handleKeyDown}}
        ...attributes
      >
        {{yield this.close}}
      </dialog>
    </MaybeInPlace>
  </template>
}

export default Dialog

/**
 * If the target element exists, will render yielded contents to it. Else, will render in place. Rendering elsewhere can
 * help with isolating styles (eg use of a font-size class like text-2xl on a parent element will not affect components
 * using this component)
 */
interface MaybeInPlaceSignature {
  Args: { targetElement?: Nullable<HTMLElement> }
  Blocks: {
    default: []
  }
}
export const MaybeInPlace: TOC<MaybeInPlaceSignature> = <template>
  {{#if @targetElement}}
    {{#in-element @targetElement}}
      {{yield}}
    {{/in-element}}
  {{else}}
    {{! A render in place option, for things like integration tests, where the target element isn't in body }}
    {{yield}}
  {{/if}}
</template>
